// Google OAuth Service for Way2Will
// Handles authentication flow for Gmail and Google Calendar

import { saveOAuthTokens, getOAuthTokens, deleteOAuthTokens, isFirestoreAvailable } from './firestoreService';

// OAuth Configuration - Set these in your .env file
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const GOOGLE_CLIENT_SECRET = import.meta.env.VITE_GOOGLE_CLIENT_SECRET || '';
const REDIRECT_URI = import.meta.env.VITE_GOOGLE_REDIRECT_URI || 'http://localhost:5173/app/connections/callback';

// OAuth Scopes
export const GOOGLE_SCOPES = {
    GMAIL_READONLY: 'https://www.googleapis.com/auth/gmail.readonly',
    GMAIL_SEND: 'https://www.googleapis.com/auth/gmail.send',
    CALENDAR_READONLY: 'https://www.googleapis.com/auth/calendar.readonly',
    CALENDAR_EVENTS: 'https://www.googleapis.com/auth/calendar.events',
    DRIVE_READONLY: 'https://www.googleapis.com/auth/drive.readonly',
    DRIVE_FILE: 'https://www.googleapis.com/auth/drive.file',
    PROFILE: 'https://www.googleapis.com/auth/userinfo.profile',
    EMAIL: 'https://www.googleapis.com/auth/userinfo.email',
};

// Service types that can be connected
export type GoogleService = 'gmail' | 'calendar' | 'drive';

// Token structure
export interface GoogleTokens {
    access_token: string;
    refresh_token?: string;
    expires_at: number;
    scope: string;
}

// Connected account info
export interface GoogleAccountInfo {
    email: string;
    name: string;
    picture?: string;
}

// Storage keys
const STORAGE_KEY_TOKENS = 'way2will_google_tokens';
const STORAGE_KEY_ACCOUNT = 'way2will_google_account';

/**
 * Get scopes for a specific service
 */
export function getScopesForService(service: GoogleService): string[] {
    const baseScopes = [GOOGLE_SCOPES.PROFILE, GOOGLE_SCOPES.EMAIL];

    switch (service) {
        case 'gmail':
            return [...baseScopes, GOOGLE_SCOPES.GMAIL_READONLY];
        case 'calendar':
            return [...baseScopes, GOOGLE_SCOPES.CALENDAR_READONLY, GOOGLE_SCOPES.CALENDAR_EVENTS];
        case 'drive':
            return [...baseScopes, GOOGLE_SCOPES.DRIVE_READONLY];
        default:
            return baseScopes;
    }
}

/**
 * Initiate OAuth flow - opens Google consent screen
 * Merges existing scopes with new service scopes to maintain multiple integrations
 */
export function initiateGoogleOAuth(service: GoogleService): void {
    if (!GOOGLE_CLIENT_ID) {
        console.error('Google Client ID not configured. Set VITE_GOOGLE_CLIENT_ID in .env');
        alert('Google OAuth not configured. Please set up credentials in .env file.');
        return;
    }

    // Get scopes for the new service
    const newScopes = getScopesForService(service);

    // Get existing scopes from stored tokens (if any)
    const existingTokens = getStoredTokens();
    const existingScopes = existingTokens?.scope ? existingTokens.scope.split(' ') : [];

    // Merge scopes - combine existing with new, removing duplicates
    const allScopes = [...new Set([...existingScopes, ...newScopes])];

    // Generate state for CSRF protection
    const state = JSON.stringify({
        service,
        timestamp: Date.now(),
        nonce: Math.random().toString(36).substring(7),
    });

    // Store state for verification on callback
    localStorage.setItem('oauth_state', state);

    // Check if we already have tokens - if so, use 'none' prompt for silent re-auth
    // Otherwise use 'select_account' to let user choose account
    const existingValidTokens = existingTokens && existingTokens.expires_at > Date.now();
    const promptType = existingValidTokens ? 'none' : 'select_account';

    // Build OAuth URL
    const params = new URLSearchParams({
        client_id: GOOGLE_CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        response_type: 'token', // Implicit Flow
        scope: allScopes.join(' '),
        state: state,
        prompt: promptType,
        // Include hint for better UX
        ...(existingTokens ? {} : {}),
    });

    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;

    // Redirect to Google OAuth
    window.location.href = authUrl;
}


/**
 * Parse tokens from URL hash (Implicit Flow)
 */
export function parseTokensFromUrl(): GoogleTokens | null {
    const hash = window.location.hash.substring(1); // Remove leading #
    const params = new URLSearchParams(hash);

    const accessToken = params.get('access_token');
    const expiresIn = params.get('expires_in');

    // Check for error in query params as well (sometimes returned there)
    const error = params.get('error');
    if (error) {
        console.error('OAuth Error:', error);
        return null;
    }

    if (!accessToken || !expiresIn) {
        return null; // No tokens found
    }

    // Get existing scopes or default
    const scope = params.get('scope') || '';

    return {
        access_token: accessToken,
        // No refresh token in implicit flow
        expires_at: Date.now() + (parseInt(expiresIn) * 1000),
        scope: scope,
    };
}

/**
 * Store tokens and fetch account info (Helper)
 */
export async function storeTokensAndFetchUser(tokens: GoogleTokens): Promise<boolean> {
    try {
        // Store tokens immediately
        localStorage.setItem('way2will_google_tokens', JSON.stringify(tokens));

        // Fetch account info with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout

        const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
            headers: { Authorization: `Bearer ${tokens.access_token}` },
            signal: controller.signal,
        });
        clearTimeout(timeoutId);

        if (response.ok) {
            const data = await response.json();
            const accountInfo: GoogleAccountInfo = {
                email: data.email,
                name: data.name,
                picture: data.picture,
            };
            localStorage.setItem('way2will_google_account', JSON.stringify(accountInfo));

            // Persist to Firestore in background (don't await)
            if (isFirestoreAvailable()) {
                const userId = localStorage.getItem('way2will_user_id') || 'demo_user';
                saveOAuthTokens(userId, tokens, accountInfo).catch(err =>
                    console.error('Background Firestore save failed:', err)
                );
            }
            return true;
        }
        return false;
    } catch (e) {
        console.error('Error storing tokens:', e);
        return false;
    }
}

/**
 * Fetch user's Google account info
 */
/**
 * Get stored tokens
 */
export function getStoredTokens(): GoogleTokens | null {
    const stored = localStorage.getItem(STORAGE_KEY_TOKENS);
    if (!stored) return null;

    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

/**
 * Get stored account info
 */
export function getStoredAccountInfo(): GoogleAccountInfo | null {
    const stored = localStorage.getItem(STORAGE_KEY_ACCOUNT);
    if (!stored) return null;

    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

/**
 * Check if tokens are still valid
 * More lenient check - allows tokens with up to 1 min buffer
 */
export function areTokensValid(): boolean {
    const tokens = getStoredTokens();
    if (!tokens) return false;

    // Check if expired (with 1 min buffer for more lenient checking)
    return tokens.expires_at > Date.now() + (60 * 1000);
}

/**
 * Check if tokens exist (even if expired) - for restoration purposes
 */
export function hasStoredCredentials(): boolean {
    const tokens = getStoredTokens();
    const account = getStoredAccountInfo();
    return !!(tokens && account);
}

/**
 * Check if a specific service is authorized
 */
export function isServiceAuthorized(service: GoogleService): boolean {
    const tokens = getStoredTokens();
    if (!tokens || !areTokensValid()) return false;

    const requiredScopes = getScopesForService(service);
    if (!tokens.scope) return false; // Handle case where scope is missing
    const authorizedScopes = tokens.scope.split(' ');

    return requiredScopes.every(scope => authorizedScopes.includes(scope));
}

/**
 * Refresh access token using refresh token
 */
export async function refreshAccessToken(): Promise<boolean> {
    const tokens = getStoredTokens();
    if (!tokens?.refresh_token) {
        // Implicit flow has no refresh token. User must re-authenticate.
        return false;
    }

    try {
        const response = await fetch('https://oauth2.googleapis.com/token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                client_id: GOOGLE_CLIENT_ID,
                client_secret: GOOGLE_CLIENT_SECRET,
                refresh_token: tokens.refresh_token,
                grant_type: 'refresh_token',
            }),
        });

        if (!response.ok) {
            console.error('Token refresh failed');
            return false;
        }

        const data = await response.json();

        const newTokens: GoogleTokens = {
            access_token: data.access_token,
            refresh_token: tokens.refresh_token, // Keep existing refresh token
            expires_at: Date.now() + (data.expires_in * 1000),
            scope: data.scope || tokens.scope,
        };

        localStorage.setItem(STORAGE_KEY_TOKENS, JSON.stringify(newTokens));

        return true;
    } catch (error) {
        console.error('Error refreshing token:', error);
        return false;
    }
}

/**
 * Revoke access and clear stored data
 */
export async function revokeGoogleAccess(): Promise<void> {
    const tokens = getStoredTokens();

    if (tokens?.access_token) {
        try {
            // Revoke token with Google
            await fetch(`https://oauth2.googleapis.com/revoke?token=${tokens.access_token}`, {
                method: 'POST',
            });
        } catch (error) {
            console.error('Error revoking token:', error);
        }
    }

    // Clear local storage
    localStorage.removeItem(STORAGE_KEY_TOKENS);
    localStorage.removeItem(STORAGE_KEY_ACCOUNT);

    // Also delete from Firestore
    if (isFirestoreAvailable()) {
        const userId = localStorage.getItem('way2will_user_id') || 'demo_user';
        await deleteOAuthTokens(userId);
    }
}

/**
 * Get a valid access token (refreshing if needed)
 */
export async function getValidAccessToken(): Promise<string | null> {
    if (!areTokensValid()) {
        const refreshed = await refreshAccessToken();
        if (!refreshed) return null;
    }

    const tokens = getStoredTokens();
    return tokens?.access_token || null;
}

/**
 * Check if OAuth is properly configured
 */
export function isOAuthConfigured(): boolean {
    return !!GOOGLE_CLIENT_ID && !!GOOGLE_CLIENT_SECRET;
}

/**
 * Load tokens from Firestore (for cross-device persistence)
 * Call this on app startup/login to restore OAuth connection
 */
export async function loadTokensFromCloud(): Promise<boolean> {
    if (!isFirestoreAvailable()) {
        console.log('[GoogleAuth] Firestore not available, using localStorage only');
        return false;
    }

    const userId = localStorage.getItem('way2will_user_id') || 'demo_user';

    try {
        const data = await getOAuthTokens(userId);

        if (!data) {
            console.log('[GoogleAuth] No cloud tokens found');
            return false;
        }

        // Store in localStorage for local use
        localStorage.setItem(STORAGE_KEY_TOKENS, JSON.stringify(data.tokens));
        localStorage.setItem(STORAGE_KEY_ACCOUNT, JSON.stringify(data.accountInfo));

        console.log('[GoogleAuth] Tokens loaded from cloud for:', data.accountInfo.email);
        return true;
    } catch (error) {
        console.error('[GoogleAuth] Error loading tokens from cloud:', error);
        return false;
    }
}
